import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';
import { SystemProcessingIndicator } from '@omilia/chat-ui';
import { colors } from '@omilia/theme';
import {
  CheckLightIcon,
  ClockIcon,
  NotificationWarningIcon,
  RedoIcon,
} from '@omilia/icon';
import { IconButton } from '@omilia/button';

import { getThemeProp } from 'styles/theme';
import { getIsPreviewMode } from 'store/services/session/selectors';
import RichContent from '../RichContent/RichContent';
import { MessageOwner, MessageStatus } from 'types';
import { fileErrorsToResend } from 'constants/fileErrors';
import { FilePreview } from 'components/FilePreview';
import {
  SInnerMessageWrapper,
  SLogo,
  SMessageStatusIcon,
  SMessageWrapper,
  SOwner,
  STextMessage,
  SThreadItemContainer,
  SUiComponentWrapper,
  SUserMessage,
  SUserMessageWrapper,
  SFileMessage,
  SFileGroup,
} from './ThreadItem.styled';
import SensitiveUserMessage from './components/sensitiveUserMessage';

import { ThreadItemProps, IUiComponent, IFile } from './ThreadItems.types';

const ThreadItem: React.FC<ThreadItemProps> = ({
  type,
  owner,
  message,
  messageId,
  onSelect,
  showAvatar,
  showOwner,
  showStatusIcon,
  status,
  ownerName,
  avatar,
  resendMessage,
  errorMessage,
  uploadFile,
  triggerScroll,
  isSensitive = false,
}) => {
  const isPreviewMode = useSelector(getIsPreviewMode);
  const [responderAvatar, setResponderAvatar] = useState(avatar);
  const theme = useContext(ThemeContext);
  const defaultLogoUrl = getThemeProp('logoUrl', { theme });
  const title = getThemeProp('title', { theme });

  // In preview mode, reflect avatar URL changes from updateConfig in real
  // time. In production, the avatar is "frozen" at render time so a later
  // config change does not retroactively rewrite past messages.
  useEffect(() => {
    if (!isPreviewMode) return;
    setResponderAvatar(avatar);
  }, [avatar, isPreviewMode]);

  const isBot = owner !== MessageOwner.USER;

  const onImageLoadError = () => setResponderAvatar(defaultLogoUrl);

  const getMessageStatusIcon = () => {
    if (status === MessageStatus.PENDING) return ClockIcon;
    if (status === MessageStatus.ERROR) return NotificationWarningIcon;
    if (status === MessageStatus.SENT) return CheckLightIcon;
  };

  const renderAvatar = () => {
    return showAvatar && isBot ? (
      <SLogo
        onError={onImageLoadError}
        src={responderAvatar}
        alt={title}
        data-test="logo"
      />
    ) : null;
  };

  const showResendButton =
    status === MessageStatus.ERROR &&
    fileErrorsToResend.some(err => err === errorMessage);

  const renderTextMessage = () => (
    <SThreadItemContainer isMessage isBot={isBot}>
      {showOwner && isBot && <SOwner>{ownerName}</SOwner>}
      <SMessageWrapper showAvatar={showAvatar}>
        {renderAvatar()}
        {isBot ? (
          <STextMessage
            role="system"
            dangerouslySetInnerHTML={{ __html: message as string }}
          />
        ) : (
          <SUserMessageWrapper>
            <SInnerMessageWrapper>
              {status === MessageStatus.ERROR && (
                <IconButton
                  size="16"
                  glyph={RedoIcon}
                  primaryColor={colors.COLOR_RED_700}
                  onClick={() => resendMessage(messageId)}
                />
              )}
              {isSensitive ? (
                <SensitiveUserMessage
                  message={message as string}
                  status={status}
                />
              ) : (
                <SUserMessage status={status} role="user">
                  {message as string}
                </SUserMessage>
              )}
            </SInnerMessageWrapper>
            {showStatusIcon && (
              <SMessageStatusIcon
                as={getMessageStatusIcon()}
                size="12"
                color={
                  status === MessageStatus.ERROR
                    ? colors.COLOR_RED_700
                    : colors.COLOR_GREY_800
                }
              />
            )}
          </SUserMessageWrapper>
        )}
      </SMessageWrapper>
    </SThreadItemContainer>
  );

  const renderPreloader = () => (
    <SThreadItemContainer isBot={isBot}>
      {renderAvatar()}
      <SystemProcessingIndicator data-test="system-processing-indicator" />
    </SThreadItemContainer>
  );

  const renderUiComponent = () => (
    <SUiComponentWrapper
      showAvatar={showAvatar}
      onTouchEndCapture={e => e.stopPropagation()}
    >
      {renderAvatar()}
      <RichContent
        message={message as IUiComponent}
        onSelect={onSelect}
        uploadFile={uploadFile}
        triggerScroll={triggerScroll}
        data-test="ui-component"
      />
    </SUiComponentWrapper>
  );

  const renderFileMessage = () => {
    return (
      <SFileMessage>
        {showResendButton && (
          <IconButton
            size="16"
            glyph={RedoIcon}
            primaryColor={colors.COLOR_RED_700}
            onClick={() =>
              resendMessage(messageId, (message as IFile).blobFiles)
            }
          />
        )}
        <SFileGroup>
          {(message as IFile).files.map((file, index) => (
            <FilePreview
              asMessage
              data={{ utterance: (message as IFile).utterance, ...file }}
              status={status}
              errorMessage={errorMessage}
              isMultipleFiles={(message as IFile).files.length > 1}
              isLast={index === (message as IFile).files.length - 1}
            />
          ))}
        </SFileGroup>
      </SFileMessage>
    );
  };

  switch (type) {
    case 'message':
      return typeof message === 'string' ? renderTextMessage() : null;
    case 'preloader':
      return isBot ? renderPreloader() : null;
    case 'ui_component':
      return typeof message === 'object' ? renderUiComponent() : null;
    case 'file':
      return typeof message === 'object' ? renderFileMessage() : null;
    default:
      return null;
  }
};

export default ThreadItem;
